import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Lightbulb, ChevronDown, ChevronUp, DollarSign, MessageSquare, Shield, HelpCircle, Info } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';

interface AdvisorNote {
  id: string;
  category: string;
  title: string;
  body: string;
  icon_name: string | null;
  sort_order: number;
}

const CATEGORY_CONFIG: Record<string, { label: string; color: string; icon: typeof DollarSign }> = {
  negotiation: { label: 'Negotiation', color: '#2563eb', icon: MessageSquare },
  cost_saving: { label: 'Save Money', color: '#16a34a', icon: DollarSign },
  questions_to_ask: { label: 'Ask Your Doctor', color: '#d97706', icon: HelpCircle },
  insurance_tip: { label: 'Insurance', color: '#9333ea', icon: Shield },
  general: { label: 'Tip', color: '#6b7280', icon: Info },
};

interface AdvisorNotesProps {
  scenarioId: string;
}

export function AdvisorNotes({ scenarioId }: AdvisorNotesProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ['advisor-notes', scenarioId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scenario_advisor_notes')
        .select('*')
        .eq('scenario_id', scenarioId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Advisor notes error:', error);
        return [];
      }
      return data as AdvisorNote[];
    },
    enabled: !!scenarioId,
  });

  const toggleNote = (id: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading || notes.length === 0) return null;

  return (
    <Card
      className={isAhp ? '' : 'border-matrix-green/20 bg-black/40 backdrop-blur-sm'}
      style={isAhp ? { backgroundColor: ahpDark ? '#0f1d2f' : '#fff', border: ahpDark ? '1px solid rgba(197,165,90,0.2)' : '1px solid rgba(27,27,27,0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' } : undefined}
    >
      <CardHeader className="pb-2">
        <CardTitle
          className={isAhp ? 'text-lg flex items-center gap-2' : 'text-lg font-mono text-matrix-green flex items-center gap-2'}
          style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? '#f0ebe3' : '#1B1B1B', fontWeight: 400 } : undefined}
        >
          <Lightbulb className="w-5 h-5" style={isAhp ? { color: '#C5A55A' } : undefined} />
          What You Should Know
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {notes.map((note) => {
            const config = CATEGORY_CONFIG[note.category] || CATEGORY_CONFIG.general;
            const isExpanded = expandedNotes.has(note.id);
            const Icon = config.icon;

            return (
              <div
                key={note.id}
                className={`rounded-lg border overflow-hidden ${isAhp ? (ahpDark ? 'border-[rgba(197,165,90,0.2)]' : 'border-gray-100') : 'border-matrix-green/10'}`}
              >
                <button
                  onClick={() => toggleNote(note.id)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 transition-colors ${
                    isAhp ? (ahpDark ? 'hover:bg-white/5' : 'hover:bg-gray-50')
                      : 'hover:bg-matrix-green/5'
                  }`}
                >
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                    style={{ backgroundColor: `${config.color}15`, color: config.color }}
                  >
                    {config.label}
                  </span>
                  <span
                    className={`text-sm flex-1 ${isAhp ? '' : 'text-white'}`}
                    style={isAhp ? { color: ahpDark ? '#f0ebe3' : '#1B1B1B' } : undefined}
                  >
                    {note.title}
                  </span>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                </button>
                {isExpanded && (
                  <div
                    className={`px-3 pb-3 text-sm leading-relaxed ${isAhp ? (ahpDark ? 'text-gray-300' : 'text-gray-600') : 'text-gray-300'}`}
                  >
                    {note.body}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
